import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, TrendingUp, DollarSign, Award, Building2, X } from 'lucide-react';
import { useAppointmentStats } from '@/hooks/useAppointmentStats';
import { useQuoteMetrics } from '@/hooks/useQuoteMetrics';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';

interface TeamLeader {
  id: string;
  full_name: string | null;
  email: string;
}

interface SalesRep {
  id: string;
  full_name: string | null;
  email: string;
  team_leader_id: string | null;
}

export const AdminMetrics = () => {
  const [selectedTeamLeader, setSelectedTeamLeader] = useState<string | null>(null);
  const [selectedSalesRep, setSelectedSalesRep] = useState<string | null>(null);
  const [teamLeaders, setTeamLeaders] = useState<TeamLeader[]>([]);
  const [salesReps, setSalesReps] = useState<SalesRep[]>([]);
  const [loading, setLoading] = useState(true);

  // Determine which user ID to filter by
  const filteredUserId = selectedSalesRep || selectedTeamLeader;

  const { stats: orgStats, loading: statsLoading } = useAppointmentStats(undefined, filteredUserId || undefined);
  const { metrics: orgMetrics, loading: metricsLoading } = useQuoteMetrics(filteredUserId || undefined);

  useEffect(() => {
    fetchUsersData();
  }, []);

  const fetchUsersData = async () => {
    try {
      setLoading(true);

      // Fetch all profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, email, team_leader_id');

      if (profilesError) throw profilesError;

      // Fetch user roles
      const { data: roles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id, role');

      if (rolesError) throw rolesError;

      // Separate team leaders and sales reps
      const leaders: TeamLeader[] = [];
      const reps: SalesRep[] = [];

      profiles?.forEach((profile) => {
        const userRole = roles?.find((r) => r.user_id === profile.id);
        if (userRole?.role === 'team_leader') {
          leaders.push({
            id: profile.id,
            full_name: profile.full_name,
            email: profile.email,
          });
        } else if (userRole?.role === 'sales_rep') {
          reps.push({
            id: profile.id,
            full_name: profile.full_name,
            email: profile.email,
            team_leader_id: profile.team_leader_id,
          });
        }
      });

      setTeamLeaders(leaders);
      setSalesReps(reps);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSalesReps = selectedTeamLeader
    ? salesReps.filter((rep) => rep.team_leader_id === selectedTeamLeader)
    : salesReps;

  const dataLoading = loading || statsLoading || metricsLoading;

  if (dataLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {[...Array(5)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-3">
                <Skeleton className="h-8 w-8" />
                <Skeleton className="h-4 w-24 mt-2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const selectedTeamLeaderName = selectedTeamLeader 
    ? teamLeaders.find(l => l.id === selectedTeamLeader)?.full_name || 
      teamLeaders.find(l => l.id === selectedTeamLeader)?.email 
    : null;
  
  const selectedSalesRepName = selectedSalesRep 
    ? salesReps.find(r => r.id === selectedSalesRep)?.full_name || 
      salesReps.find(r => r.id === selectedSalesRep)?.email 
    : null;

  const hasActiveFilters = selectedTeamLeader || selectedSalesRep;

  return (
    <div className="space-y-6">
      {/* Compact Filters in Header */}
      <div className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-muted-foreground">Filter by:</span>
          <Select
            value={selectedTeamLeader || 'all'}
            onValueChange={(value) => {
              setSelectedTeamLeader(value === 'all' ? null : value);
              setSelectedSalesRep(null);
            }}
          >
            <SelectTrigger className="w-[180px] h-9">
              <SelectValue placeholder="Team Leader" />
            </SelectTrigger>
            <SelectContent className="bg-background z-50">
              <SelectItem value="all">All Team Leaders</SelectItem>
              {teamLeaders.map((leader) => (
                <SelectItem key={leader.id} value={leader.id}>
                  {leader.full_name || leader.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={selectedSalesRep || 'all'}
            onValueChange={(value) => setSelectedSalesRep(value === 'all' ? null : value)}
          >
            <SelectTrigger className="w-[180px] h-9">
              <SelectValue placeholder="Sales Rep" />
            </SelectTrigger>
            <SelectContent className="bg-background z-50">
              <SelectItem value="all">All Sales Reps</SelectItem>
              {filteredSalesReps.map((rep) => (
                <SelectItem key={rep.id} value={rep.id}>
                  {rep.full_name || rep.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedTeamLeader(null);
                setSelectedSalesRep(null);
              }}
              className="h-9 text-xs"
            >
              Clear All
            </Button>
          )}
        </div>

        {/* Active Filter Badges */}
        {hasActiveFilters && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground">Active filters:</span>
            {selectedTeamLeaderName && (
              <Badge 
                variant="secondary" 
                className="gap-1 pr-1 hover:bg-secondary/80 cursor-pointer transition-colors"
                onClick={() => {
                  setSelectedTeamLeader(null);
                  setSelectedSalesRep(null);
                }}
              >
                Team Leader: {selectedTeamLeaderName}
                <X className="h-3 w-3 ml-1" />
              </Badge>
            )}
            {selectedSalesRepName && (
              <Badge 
                variant="secondary" 
                className="gap-1 pr-1 hover:bg-secondary/80 cursor-pointer transition-colors"
                onClick={() => setSelectedSalesRep(null)}
              >
                Sales Rep: {selectedSalesRepName}
                <X className="h-3 w-3 ml-1" />
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <Card className="hover:border-accent transition-all">
          <CardHeader className="pb-3">
            <Building2 className="h-8 w-8 text-accent mb-2" />
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Appointments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{orgStats.totalAppointments}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {orgStats.scheduledCount} scheduled
            </p>
          </CardContent>
        </Card>

        <Card className="hover:border-accent transition-all">
          <CardHeader className="pb-3">
            <TrendingUp className="h-8 w-8 text-accent mb-2" />
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Conversion Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{orgStats.conversionRate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground mt-1">
              {orgStats.closedDeals} deals closed
            </p>
          </CardContent>
        </Card>

        <Card className="hover:border-accent transition-all">
          <CardHeader className="pb-3">
            <DollarSign className="h-8 w-8 text-accent mb-2" />
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${orgMetrics.totalRevenue.toFixed(0)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {orgMetrics.totalQuotes} quotes
            </p>
          </CardContent>
        </Card>

        <Card className="hover:border-accent transition-all">
          <CardHeader className="pb-3">
            <Users className="h-8 w-8 text-accent mb-2" />
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Active Sales Reps
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{salesReps.length}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {teamLeaders.length} team leaders
            </p>
          </CardContent>
        </Card>

        <Card className="hover:border-accent transition-all">
          <CardHeader className="pb-3">
            <Award className="h-8 w-8 text-accent mb-2" />
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Avg Deal Size
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${orgMetrics.averageDealSize.toFixed(0)}</div>
            <p className="text-xs text-green-600 mt-1">
              ${orgMetrics.paidRevenue.toFixed(0)} paid
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Sales Team Overview Table */}
      <Card>
        <CardHeader>
          <CardTitle>Sales Team Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Team Leader</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSalesReps.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    No sales reps found
                  </TableCell>
                </TableRow>
              ) : (
                filteredSalesReps.map((rep) => {
                  const leader = teamLeaders.find((l) => l.id === rep.team_leader_id);
                  return (
                    <TableRow key={rep.id}>
                      <TableCell className="font-medium">
                        {rep.full_name || rep.email}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">sales_rep</Badge>
                      </TableCell>
                      <TableCell>
                        {leader ? leader.full_name || leader.email : 'Unassigned'}
                      </TableCell>
                      <TableCell className="text-right">
                        <button
                          onClick={() => {
                            setSelectedSalesRep(rep.id);
                            setSelectedTeamLeader(null);
                          }}
                          className="text-accent hover:underline text-sm"
                        >
                          View Details
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
